import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { AlertTriangle, Send, Search, CheckCircle } from 'lucide-react';
import Modal from '../shared/Modal';

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';

/**
 * Bulk portal invite — one "set your password" email per user, asking them to check their
 * traveller profile and record data-sharing consent.
 *
 * Deliberately two-step: Preview always runs first and shows the exact recipient list, because
 * sending is irreversible and goes to people outside the business.
 */
export default function PortalInviteModal({ clients = [], onClose }) {
  const [clientId, setClientId]   = useState('');
  const [domain,   setDomain]     = useState('');
  const [testEmail, setTestEmail] = useState('');

  const [preview, setPreview] = useState(null);
  const [result,  setResult]  = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [confirmText, setConfirmText] = useState('');

  const call = (data) => httpsCallable(functions, 'sendPortalInvites')(data).then(r => r.data);

  const runPreview = async () => {
    setBusy(true); setError(''); setResult(null); setConfirmText('');
    try {
      setPreview(await call({ clientId, emailDomain: domain, dryRun: true }));
    } catch (err) {
      setError(err.message || 'Preview failed.');
      setPreview(null);
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { setError('Enter an address to send the test to.'); return; }
    setBusy(true); setError('');
    try {
      const r = await call({ clientId, emailDomain: domain, dryRun: false, limitTo: [testEmail.trim()] });
      setResult({ ...r, wasTest: true });
    } catch (err) {
      setError(err.message || 'Test send failed.');
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    setBusy(true); setError('');
    try {
      setResult(await call({ clientId, emailDomain: domain, dryRun: false }));
      setPreview(null);
    } catch (err) {
      setError(err.message || 'Send failed.');
    } finally { setBusy(false); }
  };

  const canPreview = !!(clientId || domain.trim());
  const armed = preview && preview.eligibleCount > 0 && confirmText.trim().toUpperCase() === 'SEND';

  return (
    <Modal title="Send portal invites" onClose={onClose}>
      <div className="space-y-4">

        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
          <AlertTriangle size={15} className="text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Each recipient gets a one-off link to set their password, plus a prompt to check their
            traveller profile and record data-sharing consent. <strong>Password links expire after
            one hour</strong> — recipients who miss the window use <em>Forgot password?</em> instead.
            Sending cannot be undone.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="pi-client" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select id="pi-client" className={inp} value={clientId} onChange={e => { setClientId(e.target.value); setPreview(null); }}>
              <option value="">— none —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="pi-domain" className="block text-sm font-medium text-gray-700 mb-1">Email domain</label>
            <input id="pi-domain" className={inp} value={domain} placeholder="dana.org.au"
              onChange={e => { setDomain(e.target.value); setPreview(null); }} />
          </div>
        </div>
        <p className="text-xs text-gray-700 -mt-1">
          Recipients are everyone matching <strong>either</strong> rule. Resigned, inactive and STX
          staff accounts are always excluded.
        </p>

        {error && <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

        {!result && (
          <button onClick={runPreview} disabled={busy || !canPreview}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm font-semibold rounded-lg hover:bg-gray-900 disabled:opacity-50">
            <Search size={14} aria-hidden="true" /> {busy ? 'Working…' : 'Preview recipients'}
          </button>
        )}

        {preview && !result && (
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-semibold text-gray-900">
                {preview.eligibleCount} recipient{preview.eligibleCount !== 1 ? 's' : ''}
                {preview.orgName ? ` · ${preview.orgName}` : ''}
              </p>
              <p className="text-xs text-gray-700 mt-0.5">
                Links point to {preview.portalUrl} · replies directed to {preview.contactEmail}
              </p>
            </div>

            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th scope="col" className="text-left px-3 py-2 font-semibold text-gray-700">Name</th>
                    <th scope="col" className="text-left px-3 py-2 font-semibold text-gray-700">Email</th>
                    <th scope="col" className="text-left px-3 py-2 font-semibold text-gray-700">Matched by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.recipients.map(r => (
                    <tr key={r.email}>
                      <td className="px-3 py-1.5 text-gray-800">{r.name}</td>
                      <td className="px-3 py-1.5 text-gray-700">{r.email}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.matchedBy.join(' + ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(preview.clientIdOnly.length > 0 || preview.domainOnly.length > 0) && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-gray-800 space-y-1">
                <p className="font-semibold text-blue-900">Worth a look before sending</p>
                {preview.clientIdOnly.length > 0 && (
                  <p>On the client but <strong>not</strong> on the domain: {preview.clientIdOnly.join(', ')}</p>
                )}
                {preview.domainOnly.length > 0 && (
                  <p>On the domain but <strong>not</strong> on the client: {preview.domainOnly.join(', ')}</p>
                )}
              </div>
            )}

            {preview.skipped.length > 0 && (
              <details className="rounded-lg border border-gray-200 p-3">
                <summary className="text-xs font-semibold text-gray-700 cursor-pointer">
                  {preview.skipped.length} excluded
                </summary>
                <ul className="mt-2 space-y-0.5">
                  {preview.skipped.map(s => (
                    <li key={s.email} className="text-xs text-gray-700">{s.email} — {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}

            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <label htmlFor="pi-test" className="block text-sm font-medium text-gray-700">
                Send yourself a test first
              </label>
              <div className="flex gap-2">
                <input id="pi-test" className={inp} value={testEmail} placeholder="you@example.com"
                  onChange={e => setTestEmail(e.target.value)} />
                <button onClick={sendTest} disabled={busy}
                  className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-800 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
                  Send test
                </button>
              </div>
              <p className="text-xs text-gray-700">
                The address must be one of the recipients above — it sends that person's real invite.
              </p>
            </div>

            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2">
              <label htmlFor="pi-confirm" className="block text-sm font-medium text-red-900">
                Type SEND to confirm all {preview.eligibleCount} invites
              </label>
              <input id="pi-confirm" className={inp} value={confirmText} placeholder="SEND"
                onChange={e => setConfirmText(e.target.value)} />
              <button onClick={sendAll} disabled={busy || !armed}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 disabled:opacity-50">
                <Send size={14} aria-hidden="true" />
                {busy ? 'Sending…' : `Send ${preview.eligibleCount} invites`}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="rounded-lg border border-green-300 bg-green-50 p-3 flex items-start gap-2">
              <CheckCircle size={15} className="text-green-700 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-green-900">
                <strong>{result.sent.length} invite{result.sent.length !== 1 ? 's' : ''} sent</strong>
                {result.wasTest ? ' (test).' : '.'}
                {result.failed.length > 0 && ` ${result.failed.length} failed.`}
              </p>
            </div>
            {result.sent.length > 0 && (
              <p className="text-xs text-gray-700 break-words">{result.sent.join(', ')}</p>
            )}
            {result.failed.length > 0 && (
              <ul className="space-y-0.5">
                {result.failed.map(f => (
                  <li key={f.email} className="text-xs text-red-700">{f.email} — {f.reason}</li>
                ))}
              </ul>
            )}
            <button onClick={() => { setResult(null); setPreview(null); setConfirmText(''); }}
              className="text-sm text-blue-700 hover:underline">
              ← Back to preview
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
